/**
 * SynkrContext.tsx - React Context for Synkr
 * 
 * CHANGE HISTORY:
 * - 251120_1930 [pending] danielbeer: Initial creation - Phase 1 critical orchestrator
 * 
 * Provides Synkr functionality to React components.
 */

import React, { createContext, useContext, useState, useMemo, useEffect, ReactNode } from 'react';
import { SynkrManager } from './SynkrManager';
import type { SynkrConfig, SyncStatus } from './types';

/**
 * Context value interface
 */
export interface SynkrContextValue {
  manager: SynkrManager;
  status: SyncStatus | null;
  
  // Sync methods will be added as needed
  // sync: (adapterName?: string) => Promise<SyncResult>;
  // registerAdapter: (name: string, adapter: BaseSyncAdapter) => void;
  // etc.
}

const SynkrContext = createContext<SynkrContextValue | null>(null);

export interface SynkrProviderProps {
  children: ReactNode;
  actr?: any;
  loggr?: any;
  profylr?: any;
  config?: SynkrConfig;
  autoSync?: boolean;
}

/**
 * SynkrProvider - Provides Synkr context to children
 * 
 * Wrap your app or component tree with this provider to use Synkr hooks.
 * 
 * @example
 * ```tsx
 * <SynkrProvider actr={actr} loggr={loggr} autoSync={true}>
 *   <YourApp />
 * </SynkrProvider>
 * ```
 */
export const SynkrProvider: React.FC<SynkrProviderProps> = ({
  children,
  actr,
  loggr,
  profylr,
  config = {},
  autoSync = false
}) => {
  // Initialize manager (stable across renders)
  const [manager] = useState(() => {
    const instance = new SynkrManager(config);
    // Inject dependencies
    if (actr) instance['actr'] = actr;
    if (loggr) instance['loggr'] = loggr;
    if (profylr) instance['profylr'] = profylr;
    return instance;
  });
  
  const [status, setStatus] = useState<SyncStatus | null>(null);
  
  // Auto-sync if enabled
  useEffect(() => {
    if (autoSync) {
      // Start periodic sync
      manager.startPeriodicSync();
      
      return () => {
        manager.stopPeriodicSync();
      };
    }
  }, [manager, autoSync]);
  
  // Listen for sync status changes
  useEffect(() => {
    const listener = (newStatus: SyncStatus) => {
      setStatus(newStatus);
    };
    
    manager.addListener(listener);
    
    return () => {
      manager.removeListener(listener);
    };
  }, [manager]);
  
  // CRITICAL: Always wrap context value in useMemo to prevent infinite re-renders
  const value = useMemo<SynkrContextValue>(() => ({
    manager,
    status,
  }), [manager, status]);
  
  return (
    <SynkrContext.Provider value={value}>
      {children}
    </SynkrContext.Provider>
  );
};

/**
 * useSynkr - Hook to access Synkr context
 * 
 * @throws Error if used outside SynkrProvider
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { manager, status } = useSynkr();
 *   
 *   const handleSync = async () => {
 *     await manager.sync();
 *   };
 *   
 *   return (
 *     <div>
 *       <p>Status: {status?.state || 'idle'}</p>
 *       <button onClick={handleSync}>Sync Now</button>
 *     </div>
 *   );
 * }
 * ```
 */
export function useSynkr(): SynkrContextValue {
  const context = useContext(SynkrContext);
  
  if (!context) {
    throw new Error('useSynkr must be used within SynkrProvider');
  }
  
  return context;
}

export default SynkrContext;
